import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDownUp, Download, Eye, Loader2, Sparkles } from 'lucide-react';
import {
  RecruitmentSubmissionDetail,
  RecruitmentSubmissionRow,
  RecruitmentSubmissionStatus,
  RecruitmentSuggestedResult,
  staffRecruitmentService,
} from '../services/api';
import { RecruitmentAnswersView } from '../components/recruitment/RecruitmentAnswersView';

const STATUS_LABEL: Record<RecruitmentSubmissionStatus, string> = {
  NEW: 'جدید',
  EVALUATED: 'ارزیابی‌شده',
  DECIDED: 'تصمیم‌گیری‌شده',
};

const RESULT_LABEL: Record<RecruitmentSuggestedResult, string> = {
  VERY_SUITABLE: 'بسیار مناسب برای ادامه فرایند',
  SUITABLE_FOR_FOLLOWUP: 'مناسب برای مصاحبه تکمیلی',
  NEEDS_MORE_REVIEW: 'نیازمند بررسی بیشتر',
  LOW_FIT: 'تناسب پایین با موقعیت',
};

const RESULT_BADGE: Record<RecruitmentSuggestedResult, string> = {
  VERY_SUITABLE: 'bg-emerald-100 text-emerald-700',
  SUITABLE_FOR_FOLLOWUP: 'bg-sky-100 text-sky-700',
  NEEDS_MORE_REVIEW: 'bg-amber-100 text-amber-800',
  LOW_FIT: 'bg-red-100 text-red-700',
};

const RecruitmentSubmissions: React.FC = () => {
  const [rows, setRows] = useState<RecruitmentSubmissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<RecruitmentSubmissionStatus | ''>('');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RecruitmentSubmissionDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [evaluating, setEvaluating] = useState(false);
  const [decisionDraft, setDecisionDraft] = useState<{
    finalDecision: RecruitmentSuggestedResult | '';
    decisionNote: string;
  }>({ finalDecision: '', decisionNote: '' });
  const [savingDecision, setSavingDecision] = useState(false);
  const [actionError, setActionError] = useState('');

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sortByScore, setSortByScore] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number; failed: number } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const response = await staffRecruitmentService.listSubmissions({
        status: status || undefined,
        search: search || undefined,
        take: 100,
      });
      setRows(response.submissions);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const openSubmission = async (id: string) => {
    setSelectedId(id);
    setDetailLoading(true);
    setActionError('');
    try {
      const d = await staffRecruitmentService.getSubmission(id);
      setDetail(d);
      setDecisionDraft({
        finalDecision: d.finalDecision || '',
        decisionNote: d.decisionNote || '',
      });
    } catch (err: any) {
      setActionError(err?.response?.data?.message || 'دریافت جزئیات ناموفق بود.');
    } finally {
      setDetailLoading(false);
    }
  };

  const runEvaluation = async () => {
    if (!selectedId) return;
    setEvaluating(true);
    setActionError('');
    try {
      const d = await staffRecruitmentService.evaluate(selectedId);
      setDetail(d);
      await load();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || 'ارزیابی هوش مصنوعی ناموفق بود.');
    } finally {
      setEvaluating(false);
    }
  };

  const saveDecision = async () => {
    if (!selectedId || !decisionDraft.finalDecision) return;
    setSavingDecision(true);
    setActionError('');
    try {
      await staffRecruitmentService.saveDecision(selectedId, {
        finalDecision: decisionDraft.finalDecision,
        decisionNote: decisionDraft.decisionNote || undefined,
      });
      await openSubmission(selectedId);
      await load();
    } catch (err: any) {
      setActionError(err?.response?.data?.message || 'ثبت تصمیم ناموفق بود.');
    } finally {
      setSavingDecision(false);
    }
  };

  const exportExcel = async () => {
    try {
      const blob = await staffRecruitmentService.exportExcel({ status: status || undefined, search: search || undefined });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'recruitment-submissions.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('خطا در دریافت فایل اکسل');
    }
  };

  const displayRows = useMemo(() => {
    if (!sortByScore) return rows;
    return [...rows].sort((a, b) => {
      if (a.overallScore === null && b.overallScore === null) return 0;
      if (a.overallScore === null) return 1;
      if (b.overallScore === null) return -1;
      return b.overallScore - a.overallScore;
    });
  }, [rows, sortByScore]);

  const allVisibleSelected = displayRows.length > 0 && displayRows.every((r) => selectedIds.has(r.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allVisibleSelected) return new Set();
      return new Set(displayRows.map((r) => r.id));
    });
  };

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const updateRowFromDetail = (id: string, d: RecruitmentSubmissionDetail) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              status: d.status,
              overallScore: d.evaluation ? d.evaluation.overallScore : r.overallScore,
              aiSuggestedResult: d.evaluation ? d.evaluation.aiSuggestedResult : r.aiSuggestedResult,
            }
          : r,
      ),
    );
  };

  /** ارزیابی گروهی — پشت‌سرهم با concurrency محدود، تا فشار زیادی به سرویس هوش مصنوعی نیاید */
  const runBulkEvaluate = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length || bulkRunning) return;

    setBulkRunning(true);
    setBulkProgress({ done: 0, total: ids.length, failed: 0 });

    const CONCURRENCY = 2;
    let cursor = 0;
    let failed = 0;

    const worker = async () => {
      while (cursor < ids.length) {
        const id = ids[cursor++];
        try {
          const d = await staffRecruitmentService.evaluate(id);
          updateRowFromDetail(id, d);
        } catch {
          failed++;
        }
        setBulkProgress((p) => (p ? { done: p.done + 1, total: p.total, failed } : p));
      }
    };

    await Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, worker));

    setBulkRunning(false);
    setSortByScore(true);
    setSelectedIds(new Set());
  };

  const answers = detail?.answers || {};
  const narrative = (answers as any).narrative || {};

  return (
    <div className="space-y-5">
      <div className="rounded-2xl bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">متقاضیان استخدام</h1>
            <p className="mt-1 text-sm text-gray-600">فرم‌های ثبت‌شده توسط متقاضیان همکاری با «من حقیقی»</p>
          </div>
          <button
            type="button"
            onClick={exportExcel}
            className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-100"
          >
            <Download className="h-4 w-4" />
            خروجی اکسل
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-2xl bg-white p-4 shadow-sm sm:grid-cols-4">
        <input
          className="sm:col-span-2 rounded-xl border border-gray-200 px-3 py-2 text-sm"
          placeholder="جستجو بر اساس نام یا موبایل"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && load()}
        />
        <select
          className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
          value={status}
          onChange={(e) => setStatus(e.target.value as RecruitmentSubmissionStatus | '')}
        >
          <option value="">همه وضعیت‌ها</option>
          {Object.entries(STATUS_LABEL).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={load}
          className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
        >
          جستجو
        </button>
      </div>

      {selectedIds.size > 0 || bulkRunning ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4">
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold text-indigo-900">
              {selectedIds.size.toLocaleString('fa-IR')} مورد انتخاب شده
            </span>
            {bulkProgress ? (
              <span className="text-xs font-semibold text-indigo-700">
                در حال ارزیابی: {bulkProgress.done.toLocaleString('fa-IR')} از {bulkProgress.total.toLocaleString('fa-IR')}
                {bulkProgress.failed > 0 ? ` (خطا: ${bulkProgress.failed.toLocaleString('fa-IR')})` : ''}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            {!bulkRunning ? (
              <button
                type="button"
                onClick={() => setSelectedIds(new Set())}
                className="rounded-xl border border-indigo-200 bg-white px-3 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-50"
              >
                لغو انتخاب
              </button>
            ) : null}
            <button
              type="button"
              onClick={runBulkEvaluate}
              disabled={bulkRunning || selectedIds.size === 0}
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {bulkRunning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {bulkRunning ? 'در حال ارزیابی گروهی...' : 'ارزیابی گروهی با هوش مصنوعی'}
            </button>
          </div>
        </div>
      ) : null}

      <div className="rounded-2xl bg-white p-4 shadow-sm">
        {loading ? (
          <div className="py-10 text-center text-sm text-gray-500">در حال بارگذاری...</div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-500">متقاضی‌ای ثبت نشده است.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-10 px-3 py-3">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      disabled={bulkRunning}
                      className="h-4 w-4 rounded border-gray-300"
                    />
                  </th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">نام</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">موبایل</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">موقعیت شغلی</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">وضعیت</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">
                    <button
                      type="button"
                      onClick={() => setSortByScore((v) => !v)}
                      className={`inline-flex items-center gap-1 ${sortByScore ? 'text-indigo-700' : ''}`}
                      title="مرتب‌سازی بر اساس امتیاز"
                    >
                      امتیاز AI
                      <ArrowDownUp className="h-3 w-3" />
                    </button>
                  </th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">نتیجه پیشنهادی</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">تاریخ ثبت</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">جزئیات</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayRows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/50">
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(row.id)}
                        onChange={() => toggleOne(row.id)}
                        disabled={bulkRunning}
                        className="h-4 w-4 rounded border-gray-300"
                      />
                    </td>
                    <td className="px-3 py-3 font-semibold text-gray-900">
                      {row.fullName} {row.lastName}
                    </td>
                    <td className="px-3 py-3 text-gray-700" dir="ltr">
                      {row.mobile}
                    </td>
                    <td className="px-3 py-3 text-gray-600">{row.positionTitle || '-'}</td>
                    <td className="px-3 py-3">
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-700">
                        {STATUS_LABEL[row.status]}
                      </span>
                    </td>
                    <td className="px-3 py-3 font-bold text-gray-800">
                      {row.overallScore !== null ? row.overallScore.toLocaleString('fa-IR') : '-'}
                    </td>
                    <td className="px-3 py-3">
                      {row.finalDecision ? (
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${RESULT_BADGE[row.finalDecision]}`}>
                          {RESULT_LABEL[row.finalDecision]} (نهایی)
                        </span>
                      ) : row.aiSuggestedResult ? (
                        <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${RESULT_BADGE[row.aiSuggestedResult]}`}>
                          {RESULT_LABEL[row.aiSuggestedResult]}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-xs text-gray-500">{new Date(row.createdAt).toLocaleString('fa-IR')}</td>
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        onClick={() => openSubmission(row.id)}
                        className="inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2.5 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                      >
                        <Eye className="h-3.5 w-3.5" />
                        جزئیات
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedId ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => {
            setSelectedId(null);
            setDetail(null);
          }}
        >
          <div
            className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-3xl bg-white p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-black text-slate-900">جزئیات متقاضی</h3>
              <button
                type="button"
                onClick={() => {
                  setSelectedId(null);
                  setDetail(null);
                }}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"
              >
                بستن
              </button>
            </div>

            {detailLoading || !detail ? (
              <div className="py-10 text-center text-sm text-gray-500">در حال بارگذاری...</div>
            ) : (
              <div className="space-y-4">
                {actionError ? (
                  <p className="rounded-xl bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{actionError}</p>
                ) : null}

                <div className="rounded-2xl border border-amber-200 bg-amber-50/60 p-4">
                  <p className="mb-2 text-xs font-bold text-amber-800">
                    اطلاعات هویتی — این بخش هرگز وارد ارزیابی هوش مصنوعی نمی‌شود
                  </p>
                  <div className="grid grid-cols-2 gap-3 text-xs sm:grid-cols-3">
                    <div><p className="text-slate-500">نام کامل</p><p className="font-bold text-slate-800">{detail.fullName} {detail.lastName}</p></div>
                    <div><p className="text-slate-500">موبایل</p><p className="font-bold text-slate-800" dir="ltr">{detail.mobile}</p></div>
                    <div><p className="text-slate-500">ایمیل</p><p className="font-bold text-slate-800">{detail.email || '-'}</p></div>
                    <div><p className="text-slate-500">کد ملی</p><p className="font-bold text-slate-800">{detail.nationalId || '-'}</p></div>
                    <div><p className="text-slate-500">تاریخ تولد</p><p className="font-bold text-slate-800">{detail.birthDate || '-'}</p></div>
                    <div><p className="text-slate-500">وضعیت تأهل</p><p className="font-bold text-slate-800">{detail.maritalStatus || '-'}</p></div>
                    <div className="col-span-2 sm:col-span-3"><p className="text-slate-500">نشانی</p><p className="font-bold text-slate-800">{detail.address || '-'}</p></div>
                  </div>
                </div>

                <details className="rounded-2xl border border-slate-200 p-4" open>
                  <summary className="cursor-pointer text-sm font-bold text-slate-800">
                    پاسخ‌های کامل فرم (بخش‌های شرایط همکاری تا درباره همکاری با ما)
                  </summary>
                  <div className="mt-3 max-h-[32rem] overflow-auto pe-1">
                    <RecruitmentAnswersView answers={answers} />
                  </div>
                </details>

                <div className="rounded-2xl border border-indigo-200 bg-indigo-50/50 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-sm font-bold text-indigo-900">ارزیابی هوش مصنوعی (Gemini)</p>
                    <button
                      type="button"
                      onClick={runEvaluation}
                      disabled={evaluating}
                      className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-bold text-white hover:bg-indigo-700 disabled:opacity-60"
                    >
                      {evaluating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                      {evaluating ? 'در حال ارزیابی...' : detail.evaluation ? 'ارزیابی مجدد' : 'ارزیابی با هوش مصنوعی'}
                    </button>
                  </div>

                  {detail.evaluation ? (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="rounded-full bg-white px-3 py-1 text-sm font-black text-indigo-900 shadow-sm">
                          امتیاز نهایی: {detail.evaluation.overallScore.toLocaleString('fa-IR')} / ۱۰
                        </span>
                        <span className={`rounded-full px-3 py-1 text-xs font-bold ${RESULT_BADGE[detail.evaluation.aiSuggestedResult]}`}>
                          {RESULT_LABEL[detail.evaluation.aiSuggestedResult]}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {detail.evaluation.scores.map((s) => (
                          <div key={s.criterionId} className="rounded-xl bg-white p-3 text-xs shadow-sm">
                            <div className="flex items-center justify-between">
                              <p className="font-bold text-slate-800">{s.title} ({s.weight}٪)</p>
                              <p className="font-black text-indigo-700">{s.score.toLocaleString('fa-IR')}/۱۰</p>
                            </div>
                            <p className="mt-1 text-slate-500">{s.rationale}</p>
                          </div>
                        ))}
                      </div>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="rounded-xl bg-white p-3 text-xs shadow-sm">
                          <p className="mb-1 font-bold text-emerald-700">نشانه‌های مثبت یافت‌شده</p>
                          <ul className="list-disc space-y-1 pr-4 text-slate-600">
                            {detail.evaluation.positiveSignalsFound.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                        <div className="rounded-xl bg-white p-3 text-xs shadow-sm">
                          <p className="mb-1 font-bold text-red-600">موارد نیازمند بررسی</p>
                          <ul className="list-disc space-y-1 pr-4 text-slate-600">
                            {detail.evaluation.redFlagsFound.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                        <div className="rounded-xl bg-white p-3 text-xs shadow-sm">
                          <p className="mb-1 font-bold text-slate-800">نقاط قوت</p>
                          <ul className="list-disc space-y-1 pr-4 text-slate-600">
                            {detail.evaluation.strengths.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                        <div className="rounded-xl bg-white p-3 text-xs shadow-sm">
                          <p className="mb-1 font-bold text-slate-800">ریسک‌ها</p>
                          <ul className="list-disc space-y-1 pr-4 text-slate-600">
                            {detail.evaluation.risks.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                        <div className="rounded-xl bg-white p-3 text-xs shadow-sm">
                          <p className="mb-1 font-bold text-slate-800">راستی‌آزمایی در مصاحبه</p>
                          <ul className="list-disc space-y-1 pr-4 text-slate-600">
                            {detail.evaluation.verifyInInterview.map((s, i) => <li key={i}>{s}</li>)}
                          </ul>
                        </div>
                      </div>

                      <div className="rounded-xl bg-white p-3 text-xs shadow-sm">
                        <p className="mb-1 font-bold text-slate-800">جمع‌بندی هوش مصنوعی</p>
                        <p className="leading-6 text-slate-600">{detail.evaluation.reasoning}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-indigo-700">هنوز ارزیابی نشده است.</p>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-200 p-4">
                  <p className="mb-3 text-sm font-bold text-slate-800">تصمیم نهایی ادمین</p>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <select
                      value={decisionDraft.finalDecision}
                      onChange={(e) =>
                        setDecisionDraft((prev) => ({ ...prev, finalDecision: e.target.value as RecruitmentSuggestedResult }))
                      }
                      className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    >
                      <option value="">انتخاب نتیجه...</option>
                      {Object.entries(RESULT_LABEL).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <textarea
                    rows={3}
                    className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    placeholder="دلیل تصمیم ارزیاب..."
                    value={decisionDraft.decisionNote}
                    onChange={(e) => setDecisionDraft((prev) => ({ ...prev, decisionNote: e.target.value }))}
                  />
                  <button
                    type="button"
                    onClick={saveDecision}
                    disabled={!decisionDraft.finalDecision || savingDecision}
                    className="mt-3 inline-flex items-center justify-center rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-60"
                  >
                    {savingDecision ? 'در حال ذخیره...' : 'ثبت تصمیم نهایی'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default RecruitmentSubmissions;
